// Utility functions for file thumbnail generation and type detection

/**
 * Check if a file type is supported for thumbnail generation
 */
export const isThumbnailSupported = (contentType, fileName) => {
  return isImageFile(contentType, fileName) || isVideoFile(contentType, fileName) || isPdfFile(contentType, fileName);
};

/**
 * Check if a file is an image
 */
export const isImageFile = (contentType, fileName) => {
  if (contentType && contentType.toLowerCase().startsWith('image/')) return true;
  if (fileName) {
    const extension = fileName.toLowerCase().split('.').pop();
    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg', 'heic', 'heif', 'avif'];
    return imageExtensions.includes(extension);
  }
  return false;
};

/**
 * Check if a file is a video
 */
export const isVideoFile = (contentType, fileName) => {
  if (!contentType && !fileName) return false;

  // Check by MIME type first
  if (contentType && contentType.toLowerCase().startsWith("video/")) {
    return true;
  }

  // Check by file extension as fallback
  if (fileName) {
    const extension = fileName.toLowerCase().split(".").pop();
    const videoExtensions = [
      "mp4", "webm", "ogg", "avi", "mov", "tfl", "mpg", "mpeg",
      "mkv", "wmv", "m4v", "3gp", "flv", "ts", "vob"
    ];
    return videoExtensions.includes(extension);
  }

  return false;
};

/**
 * Check if a file is a PDF
 */
export const isPdfFile = (contentType, fileName) => {
  if (contentType && contentType.toLowerCase().startsWith("application/pdf")) {
    return true;
  }
  if (fileName) {
    const extension = fileName.toLowerCase().split(".").pop();
    return extension === "pdf";
  }
  return false;
};

/**
 * Generate thumbnail filename from original filename
 */
export const generateThumbnailFilename = (originalFilename) => {
  const nameWithoutExt = originalFilename.substring(0, originalFilename.lastIndexOf('.'));
  return `${nameWithoutExt}_thumb.jpg`;
};

/**
 * Generate thumbnail for supported file types
 */
export const generateThumbnail = async (file) => {
  return new Promise((resolve, reject) => {
    // Safety timeout: thumbnail generation should never block file upload for more than 7s
    const safetyTimer = setTimeout(() => {
      reject(new Error("Thumbnail generation timed out"));
    }, 7000);

    const safeResolve = (val) => {
      clearTimeout(safetyTimer);
      resolve(val);
    };

    const safeReject = (err) => {
      clearTimeout(safetyTimer);
      reject(err);
    };

    if (isImageFile(file.type, file.name)) {
      generateImageThumbnail(file).then(safeResolve).catch(safeReject);
    } else if (isVideoFile(file.type, file.name)) {
      generateVideoThumbnail(file).then(safeResolve).catch(safeReject);
    } else if (isPdfFile(file.type)) {
      generatePdfThumbnail(file).then(safeResolve).catch(safeReject);
    } else {
      clearTimeout(safetyTimer);
      safeReject(new Error("Unsupported file type for thumbnail generation"));
    }
  });
};

/**
 * Generate thumbnail for image files
 * Uses createImageBitmap when available for fast off-thread decoding that doesn't hang in background tabs
 */
const generateImageThumbnail = async (file) => {
  if (typeof window !== "undefined" && typeof window.createImageBitmap === "function") {
    try {
      const bitmap = await window.createImageBitmap(file);
      const maxSize = 200;
      let { width, height } = bitmap;

      if (width > height) {
        if (width > maxSize) {
          height = Math.round((height * maxSize) / width);
          width = maxSize;
        }
      } else {
        if (height > maxSize) {
          width = Math.round((width * maxSize) / height);
          height = maxSize;
        }
      }

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(bitmap, 0, 0, width, height);

      if (typeof bitmap.close === "function") {
        bitmap.close();
      }

      return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error("Failed to create blob from canvas"));
          }
        }, "image/jpeg", 0.8);
      });
    } catch (e) {
      // Fallback to Image element below
    }
  }

  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      const maxSize = 200;
      let { width, height } = img;

      if (width > height) {
        if (width > maxSize) {
          height = Math.round((height * maxSize) / width);
          width = maxSize;
        }
      } else {
        if (height > maxSize) {
          width = Math.round((width * maxSize) / height);
          height = maxSize;
        }
      }

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");

      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob(resolve, "image/jpeg", 0.8);
    };

    img.onerror = (err) => {
      URL.revokeObjectURL(objectUrl);
      reject(err);
    };

    img.src = objectUrl;
  });
};

/**
 * Generate thumbnail for video files
 */
const generateVideoThumbnail = (file) => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    video.onloadedmetadata = () => {
      video.currentTime = Math.min(1, video.duration * 0.1);
    };
    
    video.onseeked = () => {
      const maxSize = 200;
      let { videoWidth: width, videoHeight: height } = video;
      
      if (width > height) {
        if (width > maxSize) {
          height = (height * maxSize) / width;
          width = maxSize;
        }
      } else {
        if (height > maxSize) {
          width = (width * maxSize) / height;
          height = maxSize;
        }
      }
      
      canvas.width = width;
      canvas.height = height;
      
      ctx.drawImage(video, 0, 0, width, height);
      
      canvas.toBlob(resolve, 'image/jpeg', 0.8);
    };
    
    video.onerror = reject;
    video.src = URL.createObjectURL(file);
    video.load();
  });
};

/**
 * Generate thumbnail for PDF files
 */
const generatePdfThumbnail = (file) => {
  return new Promise((resolve) => {
    // Create a styled PDF thumbnail
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    canvas.width = 200;
    canvas.height = 260;
    
    // Red background
    ctx.fillStyle = '#dc3545';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // White document area
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(20, 20, canvas.width - 40, canvas.height - 40);
    
    // Document lines
    ctx.strokeStyle = '#e0e0e0';
    ctx.lineWidth = 1;
    for (let i = 0; i < 8; i++) {
      const y = 50 + (i * 20);
      ctx.beginPath();
      ctx.moveTo(40, y);
      ctx.lineTo(canvas.width - 40, y);
      ctx.stroke();
    }
    
    // PDF text
    ctx.fillStyle = '#dc3545';
    ctx.font = 'bold 24px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('PDF', canvas.width / 2, canvas.height - 40);
    
    canvas.toBlob(resolve, 'image/jpeg', 0.8);
  });
};

